/**
 * Shared passenger / traveller validation. See /app/memory/SKILLS.md §2.
 * Pure functions — no React, no I/O.
 *
 *   validatePassenger(pax, travelDateISO, paxType="ADULT")
 *     -> { ok: boolean, errors: { field: message } }
 *
 *   validateContact(contact)
 *     -> { ok: boolean, errors: { ... } }
 */
import { differenceInYears, addMonths, parseISO, isValid, isAfter } from "date-fns";

const NAME_RE = /^[A-Za-z\s\-']{1,60}$/;
const PASSPORT_RE = /^[A-Z0-9]{6,15}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d{8,15}$/;

export function validatePassenger(pax = {}, travelDateISO = "", paxType = "ADULT") {
  const errors = {};
  const t = travelDateISO ? parseISO(travelDateISO) : null;

  if (!pax.first_name || !NAME_RE.test(pax.first_name.trim()))
    errors.first_name = "First name (letters only, max 60).";
  if (!pax.last_name || !NAME_RE.test(pax.last_name.trim()))
    errors.last_name = "Last name (letters only, max 60).";

  // DOB + pax type alignment
  if (!pax.dob) {
    errors.dob = "Date of birth required.";
  } else {
    const dob = parseISO(pax.dob);
    if (!isValid(dob)) errors.dob = "Invalid date of birth.";
    else if (t) {
      const age = differenceInYears(t, dob);
      if (paxType === "ADULT" && age < 12)
        errors.dob = "Adult must be at least 12 years on travel date.";
      else if (paxType === "CHILD" && (age < 2 || age >= 12))
        errors.dob = "Child must be 2–11 years on travel date.";
      else if (paxType === "INFANT" && age >= 2)
        errors.dob = "Infant must be under 2 years on travel date.";
    }
  }

  if (!pax.nationality || pax.nationality.trim().length < 2)
    errors.nationality = "Nationality required.";
  if (!pax.gender) errors.gender = "Gender required.";

  if (!pax.passport_number || !PASSPORT_RE.test(pax.passport_number.trim()))
    errors.passport_number = "Passport must be 6–15 alphanumeric characters.";

  if (!pax.passport_expiry) {
    errors.passport_expiry = "Passport expiry required.";
  } else {
    const exp = parseISO(pax.passport_expiry);
    if (!isValid(exp)) errors.passport_expiry = "Invalid expiry date.";
    else if (t && !isAfter(exp, addMonths(t, 6)))
      errors.passport_expiry = "Passport must be valid for ≥ 6 months after travel date.";
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

export function validateContact(contact = {}) {
  const errors = {};
  if (!contact.email || !EMAIL_RE.test(contact.email)) errors.email = "Valid email required.";
  if (!contact.phone || !PHONE_RE.test(String(contact.phone).replace(/\s/g, "")))
    errors.phone = "Valid phone (E.164, e.g. +12025550100).";
  return { ok: Object.keys(errors).length === 0, errors };
}
